import { useEffect, useState } from 'react';
import { Copy, Check, Share2, MessageSquare, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { menuShareMessage, resolveShareLink } from '@/lib/menuShareReady';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { canNativeShare, openDraft, shareOrCopy, textUrl, whatsappUrl } from '@/lib/composeDispatch';
import { MailClientButton } from '@/components/share/MailClientButton';

export const FEATURE_META: FeatureMeta = {
  id: 'send-it-yourself',
  label: 'Send It From My Own Email Or Text',
  description:
    'Hand back a ready-made message and link so the seller can send it from their own phone or mailbox instead of through the platform sender.',
  triggerType: 'panel',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

interface SendItYourselfCardProps {
  /** The link the recipient should open. */
  url: string;
  /** Ready-made body text, without the link (the link is appended). */
  message: string;
  /** Subject line used for the email option. */
  subject: string;
  /** Optional heading override. */
  title?: string;
  helperText?: string;
  className?: string;
  /** When set, the message is editable in place. */
  onMessageChange?: (next: string) => void;
  /** The first draft, restored by "Revert to original" without a new AI call. */
  originalMessage?: string;
  /** Editor height in rows (editable mode only). */
  rows?: number;
  /** Fired once per send action on any channel (copy, share sheet, text, WhatsApp, email). */
  onDispatch?: (channel: SelfShareChannel) => void;
}

export type SelfShareChannel = 'copy' | 'native' | 'text' | 'whatsapp' | 'email';

/**
 * "Send it from my own email or text instead."
 *
 * The buyer opens something from a person they recognise rather than from a
 * platform sender. Nothing leaves through our sending stack here: the message
 * is copied to the clipboard or handed to the device (share sheet, SMS,
 * WhatsApp, mail client). Views on the link are already counted by the existing
 * share-link tracking, so the payoff still shows up.
 */
export function SendItYourselfCard({
  url,
  message,
  subject,
  title = 'Send it yourself instead',
  helperText = 'Buyers open a message from someone they know. Send this from your own phone or mailbox — the link still tracks views.',
  className,
  onMessageChange,
  originalMessage,
  rows = 5,
  onDispatch,
}: SendItYourselfCardProps) {
  useFeatureRegistration(FEATURE_META);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [message, url]);

  const fullText = menuShareMessage(message, url);
  const shareAvailable = canNativeShare();
  const smsHref = textUrl(fullText);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      onDispatch?.('copy');
      window.setTimeout(() => setCopied(false), 2000);
      toast.success('Message copied', { description: 'Paste it into a text or email.' });
    } catch {
      toast.error('Could not copy the message', { description: 'Select the text and copy it manually.' });
    }
  };

  const handleNativeShare = async () => {
    const outcome = await shareOrCopy(fullText, subject);
    if (outcome !== 'failed') onDispatch?.('native');
    if (outcome === 'copied') {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast.success('Message copied', { description: 'Your device could not open a share sheet, so the note is ready to paste.' });
    } else if (outcome === 'failed') {
      toast.error('Could not open the share sheet', { description: 'Copy the message and paste it anywhere.' });
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</Label>
        {helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
      </div>

      {onMessageChange ? (
        <div className="space-y-1.5">
          <Textarea
            aria-label="Your message"
            value={resolveShareLink(message, url)}
            rows={rows}
            maxLength={600}
            onChange={(e) => onMessageChange(e.target.value)}
            className="text-sm leading-relaxed"
          />
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{url && !resolveShareLink(message, url).includes(url) ? 'Your menu link is added at the end.' : 'This is exactly what your buyer receives.'}</span>
            {originalMessage !== undefined && resolveShareLink(message, url) !== resolveShareLink(originalMessage, url) && (
              <Button variant="quiet" size="sm" className="h-7 px-2" onClick={() => onMessageChange(originalMessage)}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revert to original
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <p className="text-sm leading-relaxed whitespace-pre-line break-words text-foreground">{fullText}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 rounded-md" data-cue="share-send" data-cue-return="share-menu">
        <Button variant="outline" size="sm" onClick={handleCopy}>
          {copied ? <Check className="w-4 h-4 mr-1.5 text-primary" /> : <Copy className="w-4 h-4 mr-1.5" />}
          {copied ? 'Copied' : 'Copy message'}
        </Button>

        {shareAvailable && (
          <Button variant="outline" size="sm" onClick={handleNativeShare}>
            <Share2 className="w-4 h-4 mr-1.5" />
            Share…
          </Button>
        )}

        {smsHref && (
          <Button variant="outline" size="sm" onClick={() => { onDispatch?.('text'); openDraft(smsHref); }}>
            <MessageSquare className="w-4 h-4 mr-1.5" />
            Text
          </Button>
        )}

        <Button variant="outline" size="sm" onClick={() => { onDispatch?.('whatsapp'); openDraft(whatsappUrl(fullText), fullText); }}>
          <MessageSquare className="w-4 h-4 mr-1.5" />
          WhatsApp
        </Button>

        <MailClientButton size="sm" variant="outline" draft={{ subject, body: fullText }} onSent={() => onDispatch?.('email')} />
      </div>
      {copied && <p role="status" className="text-sm text-muted-foreground">Message copied — ready to paste into your conversation.</p>}
    </div>
  );
}

export default SendItYourselfCard;
